import { moveCardErrorTitleKey } from './board.logic';
import {
  patchOrderWithMerge,
  useMoveCardMutation,
  useToggleCriticalMutation,
  type IPatchOrderVariables,
} from '@d-orca/shared/client/hooks';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteDeliveryOrder,
  deleteDeliveryStatus,
  deleteInventoryMaterial,
  patchDeliveryOrder,
  postDeliveryOrder,
  postDeliveryStatus,
  postInventorySnapshot,
  renameDeliveryStatus,
  reorderDeliveryStatuses,
} from '@apis/logistics';
import type { DeliveryOrderCreateBody } from '@apis/logistics';
import { isStaleRefusal, type ILogisticsRevisionHandle } from '@apis/logisticsRevision';
import {
  DELIVERED_STATUS_ID,
  type Delivery,
  type DeliveryBoardStatus,
} from '@interfaces/ILogistics';
import type { OrderPrefill } from './orderPrefill';
import type { IToast } from '@components/ui/Toasts/useToasts';
import { OWNS_ERROR_FEEDBACK, logisticsKeys } from '@d-orca/shared/client/hooks';
import { supplierSaveErrorOf } from '@d-orca/shared/client/logistics';
import { failureToast } from './logisticsFailureToast';

/** The toast title of a write with no more specific one. */
const GENERIC_SAVE_ERROR = 'logistics.pipeline.saveError';

/** The order dialog's open/seed state — a create (`initial: null`, an
 *  optional prefill) or an edit of an existing delivery. */
export type IOrderDialogState = null | {
  initial: Delivery | null;
  prefill?: OrderPrefill;
};

/**
 * The material delete, on its own: the one inventory write hosted OUTSIDE
 * this page too (the task drawer's stock companion, which has no toast stack
 * and reports the failure inline). Same key, same invalidation — `onError`
 * is the only thing a host supplies.
 */
export function useDeleteMaterial(
  siteId: string,
  { onError, revision }: { onError: (error: Error) => void; revision: ILogisticsRevisionHandle | null },
) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (materialName: string) => deleteInventoryMaterial(siteId, materialName, revision),
    /* The server also takes the material off every site-map area it was
       filed in, so the map's cache is as stale as the list's. */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['logistics', siteId] });
      void queryClient.invalidateQueries({ queryKey: ['site-map', siteId] });
    },
    // A stale refusal is the refresh dock's to announce, not the host's error.
    onError: (error) => {
      if (!isStaleRefusal(error)) onError(error);
    },
  });
}

/**
 * Every write this page makes, plus the drag-and-drop handler that is really
 * just a thin wrapper over `moveCard`.
 *
 * Pulled out of the page component verbatim — same query keys, same
 * optimistic/non-optimistic split, same error toasts. The mutations still
 * need a few pieces of the page's own state (the order dialog, the
 * in-flight delete ref) to close/stage themselves, so those are threaded in
 * as setters rather than the hook reaching for module-level state.
 */
export function useLogisticsMutations({
  siteId,
  push,
  setOrderDialog,
  setDeletingRef,
  onDelivered,
  revision,
}: {
  siteId: string;
  push: (toast: Omit<IToast, 'id'>) => void;
  setOrderDialog: Dispatch<SetStateAction<IOrderDialogState>>;
  setDeletingRef: Dispatch<SetStateAction<string | null>>;
  /**
   * An order has just been recorded as arrived — material the site now holds
   * and nobody has counted. Fired on the TRANSITION only (a card re-filed
   * inside Delivered, or an already-arrived order edited for something else,
   * is not news), and only once the PATCH has landed: an arrival that failed
   * to save must not open a dialog offering to reconcile against it.
   *
   * Both routes into the column report it — the drag and the edit form's own
   * status field — because they are the same claim made two ways.
   */
  onDelivered?: (delivery: Delivery) => void;
  /** The page's revision, sent with every write so a stale one is refused. */
  revision: ILogisticsRevisionHandle | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  /** A stale refusal is the refresh dock's to announce, not a toast's. Any
   *  other failure says why under its title. */
  const toastUnlessStale = (error: Error, title: string) => {
    if (!isStaleRefusal(error)) push(failureToast(t, error, title));
  };
  /** An order save refused on its supplier draft (a taken name, a bad phone)
   *  is shown on the supplier fields by the form; anything else toasts. */
  const onOrderError = (error: Error) => {
    if (supplierSaveErrorOf(error) === null) toastUnlessStale(error, t(GENERIC_SAVE_ERROR));
  };
  /** A landed order save may have created or edited a supplier with it. */
  const refreshSuppliers = () => void queryClient.invalidateQueries({ queryKey: logisticsKeys.suppliers(siteId) });

  const deleteMaterial = useDeleteMaterial(siteId, {
    onError: (error) => push(failureToast(t, error, t('logistics.inventory.deleteError'))),
    revision,
  });
  const createOrder = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (body: DeliveryOrderCreateBody) => postDeliveryOrder(siteId, body, revision),
    onSuccess: () => {
      setOrderDialog(null);
      void queryClient.invalidateQueries({ queryKey: ['logistics', siteId] });
      refreshSuppliers();
    },
    onError: onOrderError,
  });
  // One patch mutation serves both the edit dialog and the status-pill menu.
  /**
   * Saving the delivery, and (optionally) folding it into the running stock.
   *
   * Sequential, not parallel: the snapshot is only correct if the arrival was
   * actually recorded, so a failed PATCH must not leave stock claiming material
   * that no delivery vouches for. The snapshot write is the second step and
   * inherits the same error toast — a merge that half-lands is worse than one
   * that visibly fails. It refetches either way: when the PATCH landed and the
   * snapshot did not, the order has already moved on the server, and the
   * board must show where it really is (a retry from the old column would
   * send the move again).
   */
  const patchOrder = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (variables: IPatchOrderVariables & {
      /** The record as it stood before this edit — the only way to tell a move
       *  INTO Delivered from a save on an order that was already there. */
      initial?: Delivery;
    }) => patchOrderWithMerge({ patchDeliveryOrder, postInventorySnapshot }, siteId, revision, variables),
    onSuccess: (order, { patch, merge, initial }) => {
      setOrderDialog(null);
      refreshSuppliers();
      /* The arrival is news. Not when the merge box did the reconciling —
         that write has already folded this delivery into the running stock,
         and asking again would offer to add the same material twice.

         Reported as the record the server just WROTE, never as `initial`: the
         same submit that flips the status also carries the form's edits, and
         they are routinely the arrival's own facts. An order created with no
         amount, given "12 t" and Delivered in one save, would otherwise open
         the count dialog with nothing to merge — and renaming the material in
         that save would open the OLD material's stock with the new material's
         delivery counted into it. `initial` is consulted for one thing only:
         which column the order came from. */
      if (
        !merge &&
        initial &&
        patch.status === DELIVERED_STATUS_ID &&
        initial.status !== DELIVERED_STATUS_ID
      ) {
        onDelivered?.(order);
      }
    },
    onError: onOrderError,
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['logistics', siteId] }),
  });
  const deleteOrder = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (ref: string) => deleteDeliveryOrder(siteId, ref, revision),
    onMutate: (ref) => setDeletingRef(ref),
    onSettled: () => setDeletingRef(null),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['logistics', siteId] }),
    onError: (error) => toastUnlessStale(error, t(GENERIC_SAVE_ERROR)),
  });

  const toggleCritical = useToggleCriticalMutation({
    siteId,
    revision,
    patchDeliveryOrder,
    onError: (error) => toastUnlessStale(error, t(GENERIC_SAVE_ERROR)),
  });

  /**
   * A card dropped on another column.
   *
   * Optimistic, unlike every other write on this page: the card has to land
   * where it was dropped at the moment it is released, and waiting a round trip
   * to move it reads as a failed drag. `onSettled` refetches either way, so the
   * server stays the authority on where the card actually is.
   */
  const moveCard = useMoveCardMutation<{ delivery: Delivery; statusId: string }>({
    siteId,
    revision,
    patchDeliveryOrder,
    /* The whole record travels as a variable, not just its ref: the arrival
       report below needs the status the card moved FROM, and variables are the
       only place a mutation-level callback can read it. (A per-call
       `mutate(..., { onSuccess })` could close over it, but TanStack drops
       those callbacks when a second `mutate` starts on the same observer
       before the first resolves — two quick drops and the first card's dialog
       never opens.) */
    toMove: ({ delivery, statusId }) => ({ ref: delivery.ref, status: statusId }),
    /* The arrival, once the write has landed. The SERVER's record, not the one
       that was dragged — it is the only copy carrying the stamp the move just
       made. The transition is read off the dragged copy, which is where the
       old status still exists.

       That transition check is defense in depth: `DeliveryBoard` already drops
       a same-column move on the floor before it reaches here, so only the
       stubbed board in `LogisticsPage.gate.test.tsx` can exercise it. It stays
       because the rule belongs with the report, not with the gesture. */
    onSuccess: (order, { delivery, statusId }) => {
      if (statusId === DELIVERED_STATUS_ID && delivery.status !== DELIVERED_STATUS_ID) {
        onDelivered?.(order);
      }
    },
    /* A refused one-open move has its own title, which already says why; any
       other failure gets the reason line under the generic one. */
    onError: (moveError) => {
      if (isStaleRefusal(moveError)) return;
      const titleKey = moveCardErrorTitleKey(moveError);
      push(titleKey === GENERIC_SAVE_ERROR ? failureToast(t, moveError, t(titleKey)) : { kind: 'error', title: t(titleKey) });
    },
  });

  /* A status is not a delivery: its own title, and the reason (a taken name,
     cards still in the column, Delivered dragged to the front) beneath. */
  const onStatusError = (error: Error) => toastUnlessStale(error, t('logistics.pipeline.statusSaveError'));

  /** Column order, same optimistic reasoning as a card move — a column that
   *  snaps back for a round trip reads as a rejected drag. */
  const reorderColumns = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (order: string[]) => reorderDeliveryStatuses(siteId, order, revision),
    onMutate: async (order) => {
      await queryClient.cancelQueries({ queryKey: ['logistics-statuses', siteId] });
      const previous = queryClient.getQueryData<DeliveryBoardStatus[]>(['logistics-statuses', siteId]);
      if (previous) {
        const byId = new Map(previous.map((status) => [status.id, status]));
        // The cache was re-read after the `await` above, so a status deleted in
        // the meantime can be missing from `byId` — writing `undefined` into the
        // column list would crash the next render. Skip the optimistic write in
        // that case; `onSettled` still refetches to the real order.
        if (order.every((id) => byId.has(id))) {
          queryClient.setQueryData(
            ['logistics-statuses', siteId],
            order.map((id) => byId.get(id)!),
          );
        }
      }
      return { previous };
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(['logistics-statuses', siteId], context.previous);
      onStatusError(error);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['logistics-statuses', siteId] }),
  });

  /* Create / rename / delete are not optimistic: they are typed decisions, not
     gestures, and the dropdown they act on stays open long enough to show the
     refetched truth. */
  const createStatus = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (name: string) => postDeliveryStatus(siteId, name, revision),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['logistics-statuses', siteId] }),
    onError: onStatusError,
  });
  const renameStatus = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ statusId, name }: { statusId: string; name: string }) =>
      renameDeliveryStatus(siteId, statusId, name, revision),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['logistics-statuses', siteId] }),
    onError: onStatusError,
  });
  const deleteStatus = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (statusId: string) => deleteDeliveryStatus(siteId, statusId, revision),
    // Deliveries in a deleted column fall back to the first one, so the cards
    // move too — both caches have to be re-read, not just the column set.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['logistics-statuses', siteId] });
      void queryClient.invalidateQueries({ queryKey: ['logistics', siteId] });
    },
    onError: onStatusError,
  });

  /**
   * A card dropped on a different column, Delivered included.
   *
   * This used to intercept a drop into Delivered and open the arrival dialog
   * instead of patching — a drag is a claim that material turned up, and that
   * claim has consequences (an arrival stamp, possibly a stock merge), so it
   * seemed to want confirmation the way the inventory Sync flow asks for one.
   * In practice it read as a bug: the one column a drag couldn't just move a
   * card into. The server already makes the arrival stamp reversible — moving
   * INTO Delivered sets `deliveredAt`, moving OUT clears it — so a wrong drop
   * costs nothing to undo, the same as any other column move — except OUT of
   * Delivered into a name another open order holds (409, dedicated toast,
   * snap-back). The deliberate
   * confirm-and-merge flow still exists: editing a delivery (kebab → Update)
   * and picking Delivered from its own status field opens the same dialog and
   * offers the same merge checkbox — that path is a typed decision, not a
   * gesture, so it keeps asking.
   *
   * What the drop DOES still raise, once the write has landed, is the stock
   * reconciliation (`onDelivered`): material has turned up and nobody has
   * counted it. That prompt is not a gate — the move is already saved by the
   * time it opens, and dismissing it leaves the card exactly where it was
   * dropped.
   */
  const handleMoveCard = (delivery: Delivery, statusId: string) => {
    moveCard.mutate({ delivery, statusId });
  };

  return {
    deleteMaterial,
    createOrder,
    patchOrder,
    deleteOrder,
    moveCard,
    reorderColumns,
    createStatus,
    renameStatus,
    deleteStatus,
    handleMoveCard,
    handleToggleCritical: (delivery: Delivery, onRefused: () => void) => toggleCritical.mutate(delivery, { onError: onRefused }),
  };
}
